import React, { useState } from 'react';
import './UserManagement.css';
import { useUserManagement } from './hooks/useUserManagement';
import UserList from './components/UserList';
import UserSearch from './components/UserSearch';
import CreateUserModal from './components/CreateUserModal';

/* ==========================================================================
   Skeleton Stat Card
   ========================================================================== */
const SkeletonStatCard = () => (
  <div className="stat-card">
    <div className="skeleton" style={{ width: 48, height: 48, borderRadius: 12, flexShrink: 0 }}></div>
    <div className="stat-content">
      <div className="skeleton skeleton-text short" style={{ height: 22, marginBottom: 6 }}></div>
      <div className="skeleton skeleton-text xshort" style={{ height: 11 }}></div>
    </div>
  </div>
);

/* ==========================================================================
   UserManagement — Main Component
   ========================================================================== */
const UserManagement = () => {
  const {
    users,
    loading,
    error,
    searchQuery,
    pagination,
    filters,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleRefresh,
    blockUser,
    unblockUser,
    deleteUser
  } = useUserManagement();

  const [selectedUser, setSelectedUser] = useState(null);
  const [showStats, setShowStats] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Calculate statistics
  const activeUsers = users.filter(u => !u.isBlocked).length;
  const blockedUsers = users.filter(u => u.isBlocked).length;
  const totalUsers = pagination.total || users.length;

  /* ---- Error State (empty data + error) ---- */
  if (error && users.length === 0) {
    return (
      <div className="user-management-container">
        <div className="error-state">
          <div className="error-content">
            <i className="fas fa-exclamation-circle error-icon"></i>
            <h2>Unable to Load Users</h2>
            <p>{error}</p>
            <button onClick={handleRefresh} className="btn-primary">
              <i className="fas fa-redo"></i>
              Retry Connection
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-container">

      {/* Header */}
      <header className="section-header">
        <div className="header-title">
          <h1>User Management</h1>
          <p>Monitor and manage user accounts, status, and details.</p>
        </div>
        <div className="header-actions">
          <button
            onClick={() => setCreateModalOpen(true)}
            className="btn-primary icon-btn"
            title="Create New User"
            style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none' }}
          >
            <i className="fas fa-plus"></i>
            <span>Create User</span>
          </button>
          <button
            onClick={() => setShowStats(!showStats)}
            className="btn-secondary icon-btn"
            title="Toggle Statistics"
          >
            <i className="fas fa-chart-bar"></i>
            <span>Stats</span>
          </button>
          <button onClick={handleRefresh} className="btn-secondary icon-btn" title="Refresh Data">
            <i className="fas fa-sync-alt"></i>
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {/* Stats Grid */}
      {showStats && (
        <div className="stats-grid">
          {loading && users.length === 0 ? (
            <>
              <SkeletonStatCard />
              <SkeletonStatCard />
              <SkeletonStatCard />
            </>
          ) : (
            <>
              <div className="stat-card total">
                <div className="stat-icon total">
                  <i className="fas fa-users"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{totalUsers}</div>
                  <div className="stat-label">Total Users</div>
                </div>
              </div>
              <div className="stat-card active">
                <div className="stat-icon active">
                  <i className="fas fa-user-check"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{activeUsers}</div>
                  <div className="stat-label">Active Users</div>
                  {totalUsers > 0 && (
                    <div className="stat-sub up">
                      <i className="fas fa-arrow-up"></i>
                      {((activeUsers / totalUsers) * 100).toFixed(0)}% of total
                    </div>
                  )}
                </div>
              </div>
              <div className="stat-card blocked">
                <div className="stat-icon blocked">
                  <i className="fas fa-user-slash"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{blockedUsers}</div>
                  <div className="stat-label">Blocked Users</div>
                  {totalUsers > 0 && (
                    <div className="stat-sub down">
                      <i className="fas fa-ban"></i>
                      {((blockedUsers / totalUsers) * 100).toFixed(0)}% of total
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Content */}
      <div className="content-wrapper">
        <UserSearch
          searchQuery={searchQuery}
          onSearch={handleSearch}
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        <UserList
          users={users}
          loading={loading}
          onBlockUser={blockUser}
          onUnblockUser={unblockUser}
          onDeleteUser={deleteUser}
          selectedUser={selectedUser}
          onSelectUser={setSelectedUser}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      </div>

      <CreateUserModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleRefresh}
      />
    </div>
  );
};

export default UserManagement;
